import { Component } from '@mycolorway/tao';
import { MDCMenuSurface, Corner } from '@material/menu-surface';
import { MDCTextField } from '@material/textfield';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';

export default Component('tr-selector-field-list', {
  mixins: [eventListenerMixin],

  properties: {
    appendMenuToBody: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    animating: {
      type: Boolean,
      default: true,
    },

    highlightedItem: {
      get() {
        return this.list.highlightedItem;
      },
    },

    options: {
      get() {
        return this.list.options;
      },
    },

    filterValue: {
      set(value) {
        this.list.filterValue = value;
      },
    },

    createable: {
      set(value) {
        this.list.createable = value;
      },
    },

    totalItemCount: {
      type: Number,
      observer: 'totalItemCountChanged',
    },

    maxItemCount: {
      type: Number,
      default: Infinity,
    },
  },

  ready() {
    this.menuSurfaceRoot = this.querySelector('.mdc-menu-surface');
    this.list = this.menuSurfaceRoot.querySelector('tr-select-list');

    const searchField = this.querySelector('.search-input .mdc-text-field');
    if (searchField) {
      this.mdcSearchField = new MDCTextField(searchField);
      this.searchInput = searchField.querySelector('input');
    }

    this.mdcMenuSurface = new MDCMenuSurface(this.menuSurfaceRoot);
    this.mdcMenuSurface.setAnchorCorner(Corner.BOTTOM_LEFT);
    this.mdcMenuSurface.quickOpen = !this.animating;
    this.totalItemCountChanged(this.totalItemCount);
    this.addEventListeners();
  },

  disconnected() {
    this.stopListening();
    this.restoreMenuSurface();

    if (this.mdcMenuSurface) {
      this.mdcMenuSurface.quickOpen = true;
      this.mdcMenuSurface.destroy();
    }
    if (this.mdcSearchField) this.mdcSearchField.destroy();
  },

  beforeCache() {
    this.restoreMenuSurface();
  },

  restoreMenuSurface() {
    if (this.appendMenuToBody && this.menuSurfaceRoot && !this.contains(this.menuSurfaceRoot)) {
      this.menuSurfaceRoot.classList.remove(
        'mdc-menu-surface--open',
        'mdc-menu-surface--animating-closed',
      );
      this.appendChild(this.menuSurfaceRoot);
    }
  },

  isOpen() {
    return this.mdcMenuSurface.open;
  },

  open() {
    if (this.isOpen()) return;

    if (this.appendMenuToBody) this.mdcMenuSurface.hoistMenuToBody();

    this.namespacedTrigger('beforeOpen');

    if (this.mdcMenuSurface.open) {
      this.mdcMenuSurface.quickOpen = true;
    } else {
      // quick dirty hack
      // this.autoScrollToSelectedItem = true;
    }

    this.mdcMenuSurface.open = true;
  },

  close() {
    if (this.isOpen()) this.mdcMenuSurface.open = false;
  },

  getSelectedOptions() {
    return this.list.selected.filter(s => !!s);
  },

  addEventListeners() {
    this.listenTo(this.searchInput, 'input', (event) => {
      const { value } = event.target;
      this.namespacedTrigger('search', { detail: value, bubbles: true });
    });

    this.listenTo(this.mdcMenuSurface, {
      'MDCMenuSurface:opened': this.handleMenuSurfaceOpened.bind(this),
      'MDCMenuSurface:closed': this.handleMenuSurfaceClosed.bind(this),
    });

    this.listenTo(this.list, 'tr-select-list:select', (event) => {
      const { value } = event.detail.dataset;
      const option = this.list.options.find(item => item.value === value);

      if (option) {
        this.namespacedTrigger('select', { detail: option });
      }
    });

    this.listenTo(this.list, 'tr-select-list:create', () => {
      this.namespacedTrigger('create', { detail: this.searchInput ? this.searchInput.value : '' });
      if (this.searchInput) this.searchInput.value = '';
    });

    this.listenTo(this.list, 'mousedown', () => this.namespacedTrigger('mousedown'));

    this.listenTo(this.menuSurfaceRoot, 'keydown', this.handleKeydown.bind(this));
  },

  setAnchor(anchor) {
    this.mdcMenuSurface.setMenuSurfaceAnchorElement(anchor);
  },

  setAnchorMargin(distance) {
    this.mdcMenuSurface.setAnchorMargin(distance);
  },

  addMenuSurfaceClass(classname) {
    if (classname) this.menuSurfaceRoot.classList.add(classname);
  },

  removeMenuSurfaceClass(classname) {
    if (classname && this.menuSurfaceRoot.classList.contains(classname)) {
      this.menuSurfaceRoot.classList.remove(classname);
    }
  },

  handleMenuSurfaceOpened() {
    if (this.searchInput) this.searchInput.focus();

    if (this.animating) this.mdcMenuSurface.quickOpen = false;

    if (this.autoScrollToSelectedItem) {
      this.scrollToSelectedItem();
      this.autoScrollToSelectedItem = false;
    }

    this.namespacedTrigger('opened', {
      bubbles: true,
    });
  },

  handleMenuSurfaceClosed() {
    this.restoreMenuSurface();
    if (this.searchInput) this.searchInput.value = '';
    this.namespacedTrigger('closed', {
      bubbles: true,
    });
  },

  handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    const isEscape = event.key === 'Escape' || event.keyCode === 27;
    const arrowUp = event.key === 'ArrowUp' || event.keyCode === 38;
    const arrowDown = event.key === 'ArrowDown' || event.keyCode === 40;
    const isEnter = event.key === 'Enter' || event.keyCode === 13;

    if (isEscape) {
      if (this.searchInput) this.searchInput.blur();
    } else if (arrowUp) {
      if (this.searchInput) event.preventDefault();
      this.highlightPrevItem();
    } else if (arrowDown) {
      if (this.searchInput) event.preventDefault();
      this.highlightNextItem();
    } else if (isEnter) {
      event.preventDefault();
      event.stopPropagation();

      const { value } = this.highlightedItem ? this.highlightedItem.dataset : {};
      if (value === 'creation-message') {
        this.namespacedTrigger('create', { detail: this.searchInput.value });
        this.searchInput.value = '';
      } else if (value) {
        const selectedOption = this.list.options.find(option => option.value === value);
        if (selectedOption) {
          this.namespacedTrigger('select', { detail: selectedOption });
        }
      }
    }
  },

  setOptions(options) {
    if (this.list) this.list.setOptions(options);
    if (this.isOpen()) this.highlightFirstItem();
  },

  ...[
    'selectOption', 'unselectOption', 'clearSelected',
    'highlightFirstItem', 'highlightPrevItem', 'highlightNextItem', 'scrollToSelectedItem',
  ].reduce((methods, method) => {
    Object.assign(methods, {
      [method](...args) {
        if (this.list) this.list[method](...args);
      },
    });

    return methods;
  }, {}),

  elementInContainer(el) {
    return this.menuSurfaceRoot.contains(el);
  },

  totalItemCountChanged(totalItemCount) {
    if (totalItemCount == null || totalItemCount <= this.maxItemCount) {
      this.menuSurfaceRoot.classList.remove('over-limit');
    } else {
      this.menuSurfaceRoot.classList.add('over-limit');
    }
  },
});
